import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Connection, ConnectionTestResult, RateLimitStatus } from '../../types/connection';
import { connectionsApi } from '../../services/api/connections';
import { StatusIndicator } from '../shared/StatusIndicators';
import toast from 'react-hot-toast';

interface ConnectionTestButtonProps {
  connection: Connection;
  onTestComplete?: (result: ConnectionTestResult) => void;
  variant?: 'primary' | 'secondary' | 'icon';
  size?: 'small' | 'medium' | 'large';
  showLastResult?: boolean;
}

const RATE_LIMIT_COOLDOWN = 30; // 30 seconds

export function ConnectionTestButton({
  connection,
  onTestComplete,
  variant = 'primary',
  size = 'medium',
  showLastResult = true
}: ConnectionTestButtonProps) {
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<ConnectionTestResult | null>(null);
  // THUB-298: the rate-limit state is DERIVED from the connection plus the current time, not
  // copied into state by an effect.
  //
  // It was two useState values kept in step by two effects: one recomputed the status from props
  // and seeded a countdown, the other decremented that countdown on an interval and, at zero,
  // reached back to clear `rateLimited`. Nothing there was independent information -- every field
  // is a function of connection.testInProgress / testInProgressTtl / lastTestRateLimitAt and how
  // much time has passed. Two copies of a derivable fact meant they could disagree: the countdown
  // hitting zero cleared `rateLimited` locally even if the props still said otherwise, and a prop
  // change mid-countdown left the old countdown running.
  //
  // So the only state is a ticking clock, and everything else is computed from it. The tick runs
  // ONLY while a cooldown is actually counting down.
  const [nowSec, setNowSec] = useState(() => Date.now() / 1000);

  // An OPTIMISTIC local cooldown, for the window after our own test returns (or is 429'd) and
  // before the refreshed connection prop carries lastTestRateLimitAt. Stored as an expiry
  // timestamp rather than a countdown, so it is derivable from the clock like everything else and
  // cannot tick out of step with it.
  const [localCooldown, setLocalCooldown] = useState<
    { until: number; kind: 'rateLimited' | 'testInProgress' } | null
  >(null);

  const rateLimitStatus: RateLimitStatus = useMemo(() => {
    let testInProgress = connection.testInProgress || false;
    let rateLimited = false;
    let retryAfter: number | undefined;

    // An expired in-progress lock is not in progress.
    if (testInProgress && connection.testInProgressTtl && nowSec >= connection.testInProgressTtl) {
      testInProgress = false;
    }

    if (connection.lastTestRateLimitAt && !testInProgress) {
      const lastTestTime = new Date(connection.lastTestRateLimitAt).getTime() / 1000;
      const elapsed = nowSec - lastTestTime;
      if (elapsed < RATE_LIMIT_COOLDOWN) {
        rateLimited = true;
        retryAfter = Math.ceil(RATE_LIMIT_COOLDOWN - elapsed);
      }
    }

    // Our own just-issued cooldown wins while it is still in the future -- the server's view has
    // not reached us yet.
    if (localCooldown && nowSec < localCooldown.until) {
      return {
        testInProgress: localCooldown.kind === 'testInProgress',
        rateLimited: localCooldown.kind === 'rateLimited',
        retryAfter: Math.ceil(localCooldown.until - nowSec),
      };
    }

    return { testInProgress, rateLimited, retryAfter };
  }, [
    connection.testInProgress,
    connection.testInProgressTtl,
    connection.lastTestRateLimitAt,
    localCooldown,
    nowSec,
  ]);

  // What the button renders as the remaining seconds. Derived, so it cannot drift from the status.
  const countdown = rateLimitStatus.retryAfter ?? null;

  // Advance the clock once a second, and ONLY while something is actually counting down --
  // an idle button does no work. Reading the clock inside the callback keeps render pure
  // (react-hooks/purity); the lazy useState initializer above is the one sanctioned read.
  const isCountingDown = rateLimitStatus.rateLimited || rateLimitStatus.testInProgress;
  useEffect(() => {
    if (!isCountingDown) return;
    const timer = setInterval(() => setNowSec(Date.now() / 1000), 1000);
    return () => clearInterval(timer);
  }, [isCountingDown]);

  const handleTestConnection = useCallback(async () => {
    if (isTesting || rateLimitStatus.testInProgress || rateLimitStatus.rateLimited) {
      return;
    }

    setIsTesting(true);
    setTestResult(null);

    try {
      const result = await connectionsApi.testConnection(connection.connectionId);
      setTestResult(result);

      // Start the local cooldown from now; the derivation counts it down.
      setLocalCooldown({ until: Date.now() / 1000 + RATE_LIMIT_COOLDOWN, kind: 'rateLimited' });
      setNowSec(Date.now() / 1000);

      // Show toast notification
      const isInbound = connection.hostingType === 'hosted';
      if (result.success) {
        if (isInbound) {
          toast.success(`Configuration validated${result.responseTime ? ` (${Math.round(result.responseTime)}ms)` : ''}`);
        } else {
          toast.success(`Connection test passed${result.responseTime ? ` (${Math.round(result.responseTime)}ms)` : ''}`);
        }
      } else {
        if (isInbound) {
          toast.error(`Configuration validation failed: ${result.error || 'Unknown error'}`);
        } else {
          toast.error(`Connection test failed: ${result.error || 'Unknown error'}`);
        }
      }

      // Callback
      if (onTestComplete) {
        onTestComplete(result);
      }
    } catch (error: any) {
      // Handle rate limit errors from server
      if (error.status === 429) {
        const retryAfter = error.retryAfter || RATE_LIMIT_COOLDOWN;
        setLocalCooldown({
          until: Date.now() / 1000 + retryAfter,
          kind: error.errorCode === 'TEST_IN_PROGRESS' ? 'testInProgress' : 'rateLimited',
        });
        setNowSec(Date.now() / 1000);
        toast.error(error.message || 'Please wait before testing again');
      } else {
        const errorMessage = error.message || 'Failed to test connection';
        toast.error(errorMessage);
        setTestResult({
          success: false,
          error: errorMessage,
          testedAt: new Date().toISOString()
        });
      }
    } finally {
      setIsTesting(false);
    }
  }, [connection.connectionId, connection.hostingType, isTesting, rateLimitStatus, onTestComplete]);

  const isDisabled = isTesting || rateLimitStatus.testInProgress || rateLimitStatus.rateLimited;

  const getButtonText = () => {
    const isInbound = connection.hostingType === 'hosted';
    if (isTesting) return isInbound ? 'Validating...' : 'Testing...';
    if (rateLimitStatus.testInProgress) return isInbound ? 'Validation in progress...' : 'Test in progress...';
    if (rateLimitStatus.rateLimited && countdown !== null && countdown > 0) {
      return `Wait ${countdown}s`;
    }
    return isInbound ? 'Validate Configuration' : 'Test Connection';
  };

  const getButtonClass = () => {
    const baseClasses = "inline-flex items-center justify-center gap-2 border-none rounded font-medium transition-all duration-200";

    // Variant classes
    const variantClasses = {
      primary: `bg-primary-600 text-white hover:bg-primary-700 ${isDisabled ? '' : 'hover:bg-primary-700'}`,
      secondary: `bg-neutral-100 dark:bg-neutral-700 text-neutral-800 dark:text-neutral-200 border border-neutral-300 dark:border-neutral-600 ${isDisabled ? '' : 'hover:bg-neutral-200 dark:hover:bg-neutral-600'}`,
      icon: `bg-transparent text-primary-600 dark:text-primary-400 px-2 py-1 min-w-0 ${isDisabled ? '' : 'hover:bg-primary-100 dark:hover:bg-primary-900/30'}`
    };

    // Size classes
    const sizeClasses = {
      small: 'px-3 py-1.5 text-xs',
      medium: 'px-4 py-2 text-sm',
      large: 'px-6 py-3 text-base'
    };

    // State classes
    const stateClasses = [
      isDisabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer',
      isTesting ? 'bg-neutral-500 dark:bg-neutral-600' : ''
    ].filter(Boolean).join(' ');

    return `${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${stateClasses}`;
  };

  // Display the current test result or the last one from connection
  const displayResult = testResult || (showLastResult ? connection.connectionTestResults : null);

  const isInbound = connection.hostingType === 'hosted';
  const buttonTitle = isDisabled
    ? getButtonText()
    : isInbound
      ? 'Validate inbound connection configuration'
      : 'Test connection connectivity';

  const resultBaseClasses = "flex items-center gap-2 px-3 py-2 rounded text-sm";
  const resultStateClasses = displayResult?.success
    ? "bg-success-500/10 dark:bg-success-500/15 border border-success-500/20 dark:border-success-500/25"
    : "bg-danger-500/10 dark:bg-danger-500/15 border border-danger-500/20 dark:border-danger-500/25";

  return (
    <div className="flex flex-col gap-2">
      <button
        className={getButtonClass()}
        onClick={handleTestConnection}
        disabled={isDisabled}
        title={buttonTitle}
      >
        {variant === 'icon' ? (
          <span className="font-mono font-bold">{isTesting ? '...' : '>'}</span>
        ) : (
          <>
            {isTesting && (
              <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            )}
            <span>{getButtonText()}</span>
          </>
        )}
      </button>

      {showLastResult && displayResult && (
        <div className={`${resultBaseClasses} ${resultStateClasses}`}>
          <StatusIndicator
            status={displayResult.success ? 'healthy' : 'error'}
            label={displayResult.success ? 'Pass' : 'Fail'}
            size="small"
          />
          {displayResult.responseTime !== undefined && (
            <span className="text-xs text-neutral-500 dark:text-neutral-400">
              {Math.round(displayResult.responseTime)}ms
            </span>
          )}
          {displayResult.error && (
            <span
              className="text-xs text-danger-600 dark:text-danger-400 max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap"
              title={displayResult.error}
            >
              {displayResult.error.substring(0, 50)}...
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default ConnectionTestButton;
